import { Toggle } from "@base-ui/react/toggle"
import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group"
import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"

// A segmented control: a short, flat set of mutually exclusive choices, all
// visible at once. Use it where a Select would hide the options behind a click
// and a radio group would spend a whole row on each.
//
// The default "text" variant is a full-width pill with text labels. The "icon"
// variant is a compact, squared control of icon-only buttons — for switching how
// the same content is displayed (e.g. a table vs. card layout), a low-stakes
// choice that suits a lighter control than the text pill.
//
// Sizing is in px rather than rem because the app resets the root font size to
// 62.5% — `h-8` is 20px here, not the 32px the design calls for.
const ToggleGroup = ({
  items,
  value,
  onChange,
  ariaLabel,
  invalid = false,
  disabled = false,
  variant = "text",
}) => {
  const isIcon = variant === "icon"

  return (
    <ToggleGroupPrimitive
      aria-label={ariaLabel}
      disabled={disabled}
      value={value ? [value] : []}
      onValueChange={([next]) => {
        // A segmented control always has a selection once one is made. Base UI
        // reports an empty array when the pressed item is pressed again; keep the
        // current value rather than dropping back to nothing.
        if (next === undefined) return

        onChange(next)
      }}
      className={classNames(
        "flex items-center gap-1 border p-[2px]",
        isIcon ? "rounded-md h-[40px] w-fit" : "h-[32px] w-full rounded-full",
        // The border is always present so the invalid state doesn't shift the
        // control by a pixel. red-600 is the same error border FormTextField uses.
        "bg-gray-200",
        invalid ? "border-red-600" : "border-transparent"
      )}
      data-design-system="ToggleGroup"
    >
      {items.map((item) => {
        const selected = item.value === value

        return (
          <Toggle
            key={item.value}
            value={item.value}
            aria-label={isIcon ? item.label : undefined}
            title={isIcon ? item.label : undefined}
            className={classNames(
              "h-full transition-colors",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-800",
              isIcon
                ? "rounded-md flex w-[40px] items-center justify-center text-[24px]"
                : "flex-1 rounded-full px-3 text-center text-sm",
              selected
                ? classNames(
                    "bg-white shadow-sm",
                    isIcon ? "text-blue-600" : "font-semibold text-gray-900"
                  )
                : "text-gray-600 hover:text-gray-700"
            )}
          >
            {isIcon ? (
              <i className={item.icon} aria-hidden="true" />
            ) : (
              item.label
            )}
          </Toggle>
        )
      })}
    </ToggleGroupPrimitive>
  )
}

ToggleGroup.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      // Font Awesome class string, used by the "icon" variant (e.g.
      // "fa-solid fa-list"). The label names the button for screen readers.
      icon: PropTypes.string,
    })
  ).isRequired,
  // Null renders the group with nothing selected — the correct starting state
  // wherever the choice is deliberately not defaulted.
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  invalid: PropTypes.bool,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(["text", "icon"]),
}

export default ToggleGroup
